// Preferences pickers -- iteration 073
// Shared between onboarding wizard and preferences page.

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import * as api from "@/lib/api";
import type { SportPublic, AgeGroupPublic, NotificationPreferences } from "@dugads/shared";
import { SchoolCrest } from "@/components/school-crest";

// --- Followed Schools ---

interface SchoolRef {
  id: string;
  name: string;
  display_name: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
}

export function FollowedSchoolsPicker({
  selectedIds,
  selectedSchools,
  onChange,
}: {
  selectedIds: string[];
  selectedSchools: SchoolRef[];
  onChange: (ids: string[], schools: SchoolRef[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SchoolRef[]>([]);
  const [searching, setSearching] = useState(false);

  async function search() {
    if (query.length < 2) return;
    setSearching(true);
    try {
      const res = await api.searchSchools(query, { limit: 10 });
      setResults(res.schools.map((s) => ({ id: s.id, name: s.name, display_name: s.display_name, logo_url: s.logo_url, primary_color: s.primary_color })));
    } finally {
      setSearching(false);
    }
  }

  function toggle(school: SchoolRef) {
    const isSelected = selectedIds.includes(school.id);
    if (isSelected) {
      onChange(
        selectedIds.filter((id) => id !== school.id),
        selectedSchools.filter((s) => s.id !== school.id),
      );
    } else {
      onChange([...selectedIds, school.id], [...selectedSchools, school]);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {selectedSchools.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedSchools.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
              <SchoolCrest name={s.display_name ?? s.name} logoUrl={s.logo_url ?? null} primaryColor={s.primary_color ?? null} size={18} />
              {s.display_name ?? s.name}
              <button type="button" onClick={() => toggle(s)} className="hover:text-destructive">×</button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          placeholder="Search for a school..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          className="text-sm"
        />
        <Button type="button" variant="outline" size="sm" onClick={search} disabled={searching || query.length < 2}>
          {searching ? "..." : "Search"}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="border rounded-md max-h-48 overflow-y-auto">
          {results.map((s) => {
            const isSelected = selectedIds.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => toggle(s)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-muted/50 ${isSelected ? "bg-primary/5" : ""}`}
              >
                <span className="flex items-center gap-2">
                  <SchoolCrest name={s.display_name ?? s.name} logoUrl={s.logo_url ?? null} primaryColor={s.primary_color ?? null} size={20} />
                  {s.display_name ?? s.name}
                </span>
                {isSelected && <span className="text-xs text-primary">✓ Following</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Sports ---

export function SportsPicker({
  sports,
  selectedIds,
  onChange,
}: {
  sports: SportPublic[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  const available = sports.filter((s) => s.enabled);

  if (available.length === 0) {
    return <p className="text-sm text-muted-foreground">No sports available</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {available.map((s) => {
        const isSelected = selectedIds.includes(s.id);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => toggle(s.id)}
            data-action={`toggle-sport-${s.id}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              isSelected
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-foreground border-border hover:bg-muted/50"
            }`}
          >
            {s.name}
          </button>
        );
      })}
    </div>
  );
}

// --- Age Groups ---

export function AgeGroupsPicker({
  ageGroups,
  selectedIds,
  onChange,
}: {
  ageGroups: AgeGroupPublic[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((g) => g !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  if (ageGroups.length === 0) {
    return <p className="text-sm text-muted-foreground">Select one or more sports to see age groups</p>;
  }

  // Group by sport
  const bySport = new Map<string, AgeGroupPublic[]>();
  for (const ag of ageGroups) {
    if (!bySport.has(ag.sport_name)) bySport.set(ag.sport_name, []);
    bySport.get(ag.sport_name)!.push(ag);
  }

  return (
    <div className="flex flex-col gap-3">
      {[...bySport.entries()].map(([sportName, groups]) => (
        <div key={sportName}>
          <p className="text-xs text-muted-foreground mb-1.5">{sportName}</p>
          <div className="flex flex-wrap gap-2">
            {groups.sort((a, b) => a.sort_order - b.sort_order).map((ag) => {
              const isSelected = selectedIds.includes(ag.id);
              return (
                <button
                  key={ag.id}
                  type="button"
                  onClick={() => toggle(ag.id)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    isSelected
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:bg-muted/50"
                  }`}
                >
                  {ag.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Notification Toggles ---

const NOTIFICATION_OPTIONS: { key: keyof NotificationPreferences; label: string; description: string }[] = [
  { key: "event_reminders", label: "Event reminders", description: "Get notified before events start" },
  { key: "score_updates", label: "Score updates", description: "Live score changes for matches you follow" },
  { key: "messages", label: "Messages", description: "Direct messages and school broadcasts" },
  { key: "school_announcements", label: "School announcements", description: "General news from your schools" },
];

export function NotificationTogglesPicker({
  prefs,
  onChange,
}: {
  prefs: NotificationPreferences;
  onChange: (np: NotificationPreferences) => void;
}) {
  function toggle(key: keyof NotificationPreferences) {
    const current = prefs[key] ?? true;
    onChange({ ...prefs, [key]: !current });
  }

  return (
    <div className="flex flex-col gap-3">
      {NOTIFICATION_OPTIONS.map((opt) => {
        const enabled = prefs[opt.key] ?? true;
        return (
          <div key={opt.key} className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0">
            <div>
              <p className="text-sm font-medium">{opt.label}</p>
              <p className="text-xs text-muted-foreground">{opt.description}</p>
            </div>
            <button
              type="button"
              onClick={() => toggle(opt.key)}
              role="switch"
              aria-checked={enabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enabled ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                  enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
}
